import { AlertContainer } from "@nova/component-alert-container"
import { ButtonSecondary, ButtonTertiary, ButtonPrimary } from "@nova/component-btn"
import { Dropdown, DropdownPosition } from "@nova/component-dropdown"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { InputOutlined } from "@nova/ds-component-input"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useState, useMemo, useCallback, ReactNode } from "react"
import { setGlobalNotificationContext } from "user/profile/slice"
import { clearSelectedMissionIds } from "../redux/missions-table-slice"
import { useBulkAssignPilot } from "./PilotTrackingMissionQueries"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { pluralize } from "@nova/util-strings"

export const AssignPilotDropdown = ({
    missionIds,
    hasMixedPilotAssignment,
    defaultCheckedPilotId,
    trigger = <ButtonSecondary title="Assign Pilot" RightIcon={Icons.ArrowDropdown} />,
    subtitle = "Apply a pilot to the selected flights.",
    disabled,
    position,
}: {
    missionIds: string[]
    hasMixedPilotAssignment: boolean
    defaultCheckedPilotId?: string | null
    trigger?: ReactNode
    subtitle?: string
    disabled?: boolean
    position?: DropdownPosition
}) => {
    const dispatch = useAppDispatch()
    const [search, setSearch] = useState("")
    const [selectedPilotId, setSelectedPilotId] = useState<string | null>(
        defaultCheckedPilotId ?? null
    )
    const [warningDismissed, setWarningDismissed] = useState(false)

    const members = useAppSelector(selectWorkspaceMembers)
    const { assignPilot } = useBulkAssignPilot(missionIds)

    const filtered = useMemo(
        () =>
            !!members?.length
                ? members.filter((m) => m.fullName?.toLowerCase().includes(search.toLowerCase()))
                : [],
        [members, search]
    )

    const resetState = useCallback(() => {
        setSelectedPilotId(null)
        setSearch("")
        setWarningDismissed(false)
    }, [])

    const handleApply = useCallback(async () => {
        if (!selectedPilotId) return
        try {
            await assignPilot(selectedPilotId)
            dispatch(clearSelectedMissionIds())
            dispatch(
                setGlobalNotificationContext({
                    message: `Pilot assigned to ${pluralize({ count: missionIds.length, str: "flight" })}.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 3000,
                })
            )
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to assign pilot. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }, [selectedPilotId, assignPilot, missionIds.length, dispatch])

    return (
        <Dropdown
            onClose={resetState}
            onOpenChange={() => setSelectedPilotId(defaultCheckedPilotId ?? null)}
            disabled={disabled}
        >
            <Dropdown.Trigger as="div">{trigger}</Dropdown.Trigger>
            <Dropdown.Menu
                unstyled
                className="w-[300px] rounded-xl bg-gray-700 shadow-dp4 overflow-hidden"
                position={position}
            >
                <div className="flex flex-col gap-1 px-4 pt-4 pb-3">
                    <span className="text-white font-medium">Assign Pilot</span>
                    <span className="text-sm text-gray-400">{subtitle}</span>
                </div>
                {hasMixedPilotAssignment && !warningDismissed && (
                    <div className="px-4 pb-3">
                        <AlertContainer
                            type="warning"
                            title="Mixed Pilot Assignments"
                            body={
                                <small className="text-xs text-gray-100">
                                    Some flights already have a pilot assigned. Applying a new pilot
                                    will overwrite it for all selected flights.
                                </small>
                            }
                            closeButtonOverride={
                                <Icons.LargeIcon
                                    Icon={Icons.Close}
                                    className="!m-0 text-gray-100 hover:text-white transition-colors cursor-pointer"
                                    iconClassName="!m-0"
                                    onClick={() => setWarningDismissed(true)}
                                />
                            }
                        />
                    </div>
                )}
                <div className="px-4 pb-3 text-white">
                    <InputOutlined
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        placeholder="Search for a pilot"
                        leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} className="mr-1" />}
                        autoFocus
                    />
                </div>
                <ScrollableArea className="max-h-[300px]">
                    {filtered.map((member) => (
                        <Dropdown.Item
                            key={member.userId}
                            clickBehaviour="keep-open"
                            onClick={() => setSelectedPilotId(member.userId)}
                            className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-500 transition-colors cursor-pointer"
                        >
                            <div
                                className={classNames(
                                    "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
                                    {
                                        "border-blue-400": selectedPilotId === member.userId,
                                        "border-gray-400": selectedPilotId !== member.userId,
                                    }
                                )}
                            >
                                {selectedPilotId === member.userId && (
                                    <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                                )}
                            </div>
                            <div className="w-7 h-7 min-w-[28px] rounded-lg overflow-hidden">
                                <ProfilePhoto userId={member.userId} fullName={member.fullName} />
                            </div>
                            <span className="text-sm text-white">{member.fullName}</span>
                        </Dropdown.Item>
                    ))}
                </ScrollableArea>
                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-600">
                    <Dropdown.Close as={ButtonTertiary} title="Cancel" />
                    <Dropdown.Close
                        as={ButtonPrimary}
                        title="Apply"
                        disabled={!selectedPilotId}
                        onClick={handleApply}
                        closeBehaviour="wait"
                    />
                </div>
            </Dropdown.Menu>
        </Dropdown>
    )
}
